import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ChorusHomepage from './components/ChorusHomepage';
import UserApproval from './components/UserApproval';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import JoinUs from './components/auth/JoinUs';
import LocalStorageDebug from './components/LocalStorageDebug';
import EnhancedUserManagement from './components/UserManagement/EnhancedUserManagement'; // Import the enhanced user management component
import EnhancedEventManagement from './components/EventManagement/EnhancedEventManagement'; // Import the enhanced event management component
import EnhancedHeadlineManagement from './components/HeadlineManagement/EnhancedHeadlineManagement'; // Import the enhanced headline management component
import EnhancedChoirManagement from './components/ChoirManagement/EnhancedChoirManagement'; // Import the enhanced choir management component
import EnhancedLeadershipManagement from './components/LeadershipManagement/EnhancedLeadershipManagement'; // Import the enhanced leadership management component
import EnhancedSchoolManagement from './components/SchoolManagement/EnhancedSchoolManagement'; // Import the new enhanced school management component
import EnhancedReminderManagement from './components/RemindersManagement/EnhancedReminderManagement'; // Import the enhanced reminder management component
import EnhancedAchievementManagement from './components/AchievementsManagement/EnhancedAchievementManagement'; // Import the enhanced achievement management component
import EnhancedQuickAccessManagement from './components/QuickAccessManagement/EnhancedQuickAccessManagement'; // Import the enhanced quick access management component
import { GalleryManagement, GalleryEventDisplay } from './components/GalleryManagement'; // Import the gallery management component
import ChoirDisplay from './components/ChoirManagement/ChoirDisplay'; // Import the public choir display component
import LeadershipDisplay from './components/LeadershipManagement/LeadershipDisplay'; // Import the public leadership display component
import './App.css';

// Protected route component that checks if user is authenticated and has required role
const ProtectedRoute = ({ children, requiredRoles = ['ADMIN', 'TEACHER'] }: { children: React.ReactElement, requiredRoles?: string[] }) => {
  const storedUser = localStorage.getItem('user');

  // If no user is found in localStorage, redirect to login
  if (!storedUser) {
    return <Navigate to="/login" replace />;
  }

  try {
    const userData = JSON.parse(storedUser);
    let hasRequiredRole = false;

    // Check for both role (string) and roles (array) formats
    if (userData.roles && Array.isArray(userData.roles)) {
      // If roles array exists, check if any role matches required roles
      hasRequiredRole = userData.roles.some((role: string) =>
        requiredRoles.includes(role.toUpperCase())
      );
    } else if (userData.role) {
      // If single role string exists, check if it matches any required role
      hasRequiredRole = requiredRoles.includes(userData.role.toUpperCase());
    }

    if (!hasRequiredRole) {
      return <Navigate to="/" replace />;
    }
  } catch (e) {
    // If parsing fails, redirect to login
    return <Navigate to="/login" replace />;
  }

  // If authenticated with required role, render the protected component
  return children;
};

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/join-us" element={<JoinUs />} />
          <Route path="/debug" element={<LocalStorageDebug />} />

          {/* Protected admin routes */}
          <Route path="/admin/users" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedUserManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/events" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedEventManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/school" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedSchoolManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/choirs" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedChoirManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/leadership" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedLeadershipManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/headlines" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedHeadlineManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/reminders" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedReminderManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/achievements" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedAchievementManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/quick-access" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <EnhancedQuickAccessManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/approvals" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <UserApproval />
            </ProtectedRoute>
          } />
          <Route path="/admin/gallery" element={
            <ProtectedRoute requiredRoles={['ADMIN', 'TEACHER']}>
              <GalleryManagement />
            </ProtectedRoute>
          } />

          {/* Public routes for displays */}
          <Route path="/gallery-event-display" element={<GalleryEventDisplay />} />
          <Route path="/choir-display" element={<ChoirDisplay />} />
          <Route path="/leadership-display" element={<LeadershipDisplay />} />

          {/* Default homepage route */}
          <Route path="/" element={<ChorusHomepage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
